import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ExternalLinkIcon, GraduationCapIcon, UserRoundIcon } from 'lucide-react';
import type { Project, ResolvedBrand, ToolCategory } from '../types/project';
import { tutorials } from '../data/tutorials';
import { BrandLogo } from './BrandLogo';
import { ToolCard } from './ToolCard';
import { ScreenshotCard } from './ScreenshotCard';
import { DocumentRow } from './DocumentRow';
interface ProjectWorkspaceProps {
  project: Project;
  brand: ResolvedBrand;
}
export function ProjectWorkspace({
  project,
  brand
}: ProjectWorkspaceProps) {
  const categories = useMemo(() => {
    const unique: ToolCategory[] = [];
    project.tools.forEach(tool => {
      if (!unique.includes(tool.category)) unique.push(tool.category);
    });
    return unique;
  }, [project]);
  const [activeCategory, setActiveCategory] = useState<ToolCategory | 'All'>('All');
  const visibleTools = activeCategory === 'All' ? project.tools : project.tools.filter(tool => tool.category === activeCategory);
  const filters: (ToolCategory | 'All')[] = ['All', ...categories];
  const headingStyle: React.CSSProperties = {
    fontFamily: 'var(--b-display)',
    color: 'var(--b-text)',
    textTransform: brand.displayUppercase ? 'uppercase' : 'none',
    letterSpacing: brand.displayUppercase ? '0.02em' : '-0.01em'
  };
  return <motion.main key={project.id} initial={{
    opacity: 0,
    y: 14
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    ease: [0.22, 1, 0.36, 1]
  }} className="mx-auto w-full max-w-6xl px-6 pb-24 pt-12" style={{
    fontFamily: 'var(--b-body)'
  }} data-id="element-126" data-mp="3jcjorw1nwst8">
      <div className="max-w-3xl" data-id="element-127" data-mp="y0rxsv324y1">
        <BrandLogo brand={brand} size="lg" data-id="element-128" data-mp="x997iml41p6" />

        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.24em]" style={{
        color: 'var(--b-accent)'
      }} data-id="element-129" data-mp="5x3zpl2g6p9p">
          {project.stage} · Updated {project.updated}
        </p>
        <h1 className="mt-3 text-4xl font-bold leading-tight sm:text-5xl" style={headingStyle} data-id="element-130" data-mp="3hcnu9y8wxw5j">
          {project.name}
        </h1>
        <p className="mt-2 text-sm font-semibold" style={{
        color: 'var(--b-accent)'
      }} data-id="element-131" data-mp="3cfnnfl48opdd">
          {project.client}
        </p>
        <p className="mt-4 text-sm leading-relaxed text-[var(--b-dim)]" data-id="element-132" data-mp="3r537yjd6fykm">{project.summary}</p>

        <div className="mt-6 flex flex-wrap items-center gap-2" data-id="element-133" data-mp="3ma784x1p86wy">
          <span style={{
          borderRadius: 'var(--b-radius)'
        }} className="inline-flex items-center gap-2 border border-[var(--b-border)] bg-[var(--b-surface)] px-3 py-1.5 text-xs text-[var(--b-dim)]" data-id="element-134" data-mp="3wzm3dmzfr2d">
            <UserRoundIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-135" data-mp="dudzt5hiu1qz" />
            Lead: {project.owner}
          </span>
          <a href={brand.site} target="_blank" rel="noreferrer noopener" style={{
          borderRadius: 'var(--b-radius)'
        }} className="inline-flex items-center gap-2 border border-[var(--b-border)] bg-[var(--b-surface)] px-3 py-1.5 text-xs font-medium text-[var(--b-text)] transition hover:border-[var(--b-accent)] hover:text-[var(--b-accent)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--b-accent)]" data-id="element-136" data-mp="2fntpo05sbckw">
            <ExternalLinkIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-137" data-mp="gf27euv4n8ph" />
            {brand.site.replace('https://', '')}
          </a>
        </div>
      </div>

      <section aria-labelledby="tools-heading" className="mt-14" data-id="element-138" data-mp="7enb70gmk977">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-139" data-mp="biblvs0jzkwo">
          <div data-id="element-140" data-mp="3tnetpkhwmb0s">
            <h2 id="tools-heading" className="text-2xl font-bold" style={headingStyle} data-id="element-141" data-mp="3tnyagxvtitd8">
              Tools &amp; logins
            </h2>
            <p className="mt-1 text-xs text-[var(--b-dim)]" data-id="element-142" data-mp="2hngflp86sve">
              Opens in a new tab. Shared logins are noted on the card.
            </p>
          </div>

          <div role="tablist" aria-label="Filter tools by category" className="flex flex-wrap gap-1.5" data-id="element-143" data-mp="1wvodq254n7td">
            {filters.map(filter => {
            const active = activeCategory === filter;
            return <button key={filter} role="tab" aria-selected={active} type="button" onClick={() => setActiveCategory(filter)} style={active ? {
              backgroundColor: 'var(--b-accent)',
              borderColor: 'var(--b-accent)',
              color: 'var(--b-on-accent)'
            } : {
              backgroundColor: 'var(--b-surface)',
              borderColor: 'var(--b-border)',
              color: 'var(--b-dim)'
            }} className="rounded-full border px-3 py-1.5 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--b-accent)]" data-id="element-144" data-mp="3jxl6uzpq0n9b">
                  {filter}
                </button>;
          })}
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-145" data-mp="1bpq4qo82nzyc">
          {visibleTools.map(tool => <React.Fragment key={tool.id} data-id="element-146" data-mp="1fqa1vrsnb3kh">
              <ToolCard tool={tool} data-id="element-147" data-mp="1fp8lr8wrvqvw" />
              {tool.screenshot && <ScreenshotCard screenshot={tool.screenshot} data-id="element-148" data-mp="j2lrxx2qmhc0" />}
            </React.Fragment>)}
        </div>
      </section>

      <section aria-labelledby="tutorials-heading" className="mt-16" data-id="element-149" data-mp="nyau88656cc0">
        <h2 id="tutorials-heading" className="text-2xl font-bold" style={headingStyle} data-id="element-150" data-mp="s0fpfbc8nh0r">
          Tutorials
        </h2>
        <p className="mt-1 text-xs text-[var(--b-dim)]" data-id="element-151" data-mp="wutnjliac294">
          Learn the shared tooling before you start working the pipeline.
        </p>

        <div className="mt-6 grid gap-4 sm:grid-cols-2" data-id="element-152" data-mp="4bwjv3kkpicd">
          {tutorials.map(tutorial => <a key={tutorial.id} href={tutorial.url} target="_blank" rel="noreferrer noopener" style={{
          borderRadius: 'var(--b-radius)'
        }} className="group flex items-start gap-4 border border-[var(--b-border)] bg-[var(--b-surface)] p-5 transition hover:-translate-y-0.5 hover:border-[var(--b-accent)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--b-accent)]" data-id="element-153" data-mp="17mk1n564uuxo">
              <span style={{
            borderRadius: 'var(--b-radius)',
            backgroundColor: 'var(--b-tint)'
          }} className="flex h-10 w-10 shrink-0 items-center justify-center border border-[var(--b-border)]" data-id="element-154" data-mp="jpmpxdchypah">
                <GraduationCapIcon className="h-4 w-4 text-[var(--b-accent)]" aria-hidden="true" data-id="element-155" data-mp="tmrgdpddl6x3" />
              </span>

              <span className="min-w-0 flex-1" data-id="element-156" data-mp="25391itbivn4f">
                <span className="block text-sm font-semibold text-[var(--b-text)]" data-id="element-157" data-mp="252g44iyge9yy">
                  {tutorial.name}
                </span>
                <span className="mt-1 block text-xs leading-relaxed text-[var(--b-dim)]" data-id="element-158" data-mp="2hl1uwhf2anj7">
                  {tutorial.description}
                </span>
                <span className="mt-2 block font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--b-dim)]" data-id="element-159" data-mp="22pvovufyuopv">
                  {tutorial.source}
                </span>
              </span>

              <ArrowUpRightIcon className="h-4 w-4 shrink-0 text-[var(--b-dim)] transition group-hover:text-[var(--b-accent)]" aria-hidden="true" data-id="element-160" data-mp="2vdi4y26drltm" />
            </a>)}
        </div>
      </section>

      <section aria-labelledby="docs-heading" className="mt-16" data-id="element-161" data-mp="2qlhxzprw7snj">
        <h2 id="docs-heading" className="text-2xl font-bold" style={headingStyle} data-id="element-162" data-mp="2qjx6k90vkptw">
          Shared documents
        </h2>
        <p className="mt-1 text-xs text-[var(--b-dim)]" data-id="element-163" data-mp="3j212bmnw6e6x">
          Everything linked here is visible to everyone on this project.
        </p>

        <div style={{
        borderRadius: 'var(--b-radius)'
      }} className="mt-6 overflow-hidden border border-[var(--b-border)] bg-[var(--b-surface)]" data-id="element-164" data-mp="1j0us6wg9bog1">
          <ul className="divide-y divide-[var(--b-border)]" data-id="element-165" data-mp="1a6y9jrmawu5d">
            {project.documents.map(document => <DocumentRow key={document.id} document={document} data-id="element-166" data-mp="159prrs52es4n" />)}
          </ul>
        </div>
      </section>
    </motion.main>;
}